'use client';

import { runtimeConfig } from '@/config/runtime-config';
import { TelegramCurrencyResponse, TelegramProductShortResponseDto, useTelegramCurrenciesFindMany } from '@/generated';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useMemo } from 'react';
import { renderProductBadge, renderProductProfitBadge } from './productBadgeStyles';

export function formatPrice(amount: string | number) {
  return Number.parseInt(amount + '').toLocaleString('fr-FR');
}

type ProductPriceSurface = 'listing' | 'detail';

type Props = {
  product: Partial<TelegramProductShortResponseDto>;
  surface?: ProductPriceSurface;
};

const useResolvedCurrencies = (product: Partial<TelegramProductShortResponseDto>) => {
  const { data: cur = [] } = useTelegramCurrenciesFindMany();
  const currencies = cur as TelegramCurrencyResponse[];

  return useMemo(() => {
    let current = currencies.find((val) => val.currency == product.currency);
    const original = currencies.find((val) => val.currency == product.originalCurrency);

    if (!current) {
      current = currencies.find((val) => val.currency == runtimeConfig.defaultCountry.currency);
    }

    return {
      current,
      original,
    };
  }, [currencies, product.currency, product.originalCurrency]);
};

export const ProductPrice: React.FC<Props> = ({ product, surface = 'listing' }) => {
  const t = useTranslations('product');
  const actualCurrencies = useResolvedCurrencies(product);
  const isNan = Number.isNaN(formatPrice(product?.price || '')) || formatPrice(product?.price || '') === 'NaN';

  const suffix = product?.priceSuffix?.name;

  if (product.priceMode === 'FREE') {
    return (
      <span className='min-w-0 whitespace-normal'>
        {surface === 'detail' ? t('take_for_free') : t('for_free_listing')}
      </span>
    );
  }

  if (product.priceMode === 'NEGOTIABLE') {
    if (surface === 'listing') return null;
    return <span className='min-w-0 whitespace-normal'>{t('negotiable_price')}</span>;
  }

  if (isNan) {
    return '';
  }

  return (
    <span className='min-w-0 whitespace-normal'>
      <span className='whitespace-nowrap'>
        {formatPrice(product?.price || '')} {actualCurrencies.current?.sign}
      </span>

      {suffix && (
        <>
          {' '}
          <wbr />
          <span className='text-nowrap whitespace-nowrap'>/{suffix}</span>
        </>
      )}
    </span>
  );
};

type StorePriceProps = {
  product: TelegramProductShortResponseDto;
  className?: string;
};

const getBadges = (product: TelegramProductShortResponseDto, t: any) => {
  const isBargain = product.isBargain;
  const isUrgentSale = product.isUrgentSale;

  const resultArr: any[] = [];

  if (isBargain && isUrgentSale) {
    resultArr.push(
      renderProductBadge({
        key: 'urgent-negotiable',
        icon: <Image src='/assets/icons/urgent-icon.svg' width={14} height={14} alt='' />,
        text: t('urgent_negotiable'),
      }),
    );
  } else {
    if (isBargain) {
      resultArr.push(
        renderProductBadge({
          key: 'negotiable',
          icon: <Image src='/assets/icons/bargain-new.svg' width={14} height={14} alt='' />,
          text: t('negotiable'),
        }),
      );
    }
    if (isUrgentSale) {
      resultArr.push(
        renderProductBadge({
          key: 'urgent-sale',
          icon: <Image src='/assets/icons/urgent-icon.svg' width={14} height={14} alt='' />,
          text: t('urgent_sale'),
        }),
      );
    }
  }

  if (product.discountPercent) {
    resultArr.push(renderProductProfitBadge(product.discountPercent, t('profit_badge_suffix')));
  }

  return resultArr;
};

export const ProductStorePrice: React.FC<StorePriceProps> = ({ product }) => {
  const t = useTranslations('product');

  const badges = getBadges(product, t);

  const actualCurrencies = useResolvedCurrencies(product);
  const hasStorePrice = product.storePrice != null;
  const formattedStorePrice = hasStorePrice ? formatPrice(product.storePrice!) : '';
  const showStorePrice = hasStorePrice && formattedStorePrice !== 'NaN' && !Number.isNaN(Number(product.storePrice));

  if (!showStorePrice && badges.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-[8px] mt-[8px]'>
      {showStorePrice && (
        <span className='text-[16px] font-medium leading-[20px] '>
          {t('store_price_label')}: {formattedStorePrice}{' '}
          {actualCurrencies.original?.sign || actualCurrencies.current?.sign}
        </span>
      )}

      {badges.length > 0 && <div className='flex gap-[6px]'>{badges}</div>}
    </div>
  );
};
